/**
 * Admin Store - חנות ניהול
 * Zustand store for admin operations state management
 */

import { create } from 'zustand'
import { api, handleApiError } from '../services/api'
import type {
  AdminUser,
  UserFilters,
  UserListQuery,
  UpdateUserStatusRequest,
  RiskScoreMapping,
  CreateRiskMappingRequest,
  UpdateRiskMappingRequest,
  ApproveRiskMappingRequest,
  RiskMappingImpactPreview,
  PendingQuestionnaire,
  QuestionnaireApprovalRequest,
  QuestionnaireWithUserDetails,
  AuditLog,
  AuditLogQuery,
  ExportAuditLogsRequest,
  AuditLogStats,
  AdminUsersResponse,
  RiskMappingsResponse,
  PendingQuestionnairesResponse,
  AuditLogsResponse,
  AuditStatsResponse,
  ClaudeModel,
  ModelsResponse,
  BudgetStatus,
  UserCostSummary,
  UserCostBreakdown,
  RevenueReport,
  SubscriptionPrices,
  AdminSubscriber,
  SubscribersResponse,
  Coupon,
  CouponInput,
  StudentSettings,
  LearningTopicAdmin,
  ExamQuestionAdmin,
  ClassProgressRow,
  ClassProgressSummary,
  ClassCode,
  TrackAdmin,
  TrackQuestionRow,
  SubAdmin,
  AssignableTrack,
  SubAdminsResponse,
} from '../types'
import type { TrackKey } from '../config/tracks'

interface AdminState {
  // ==================== User Management State ====================
  users: AdminUser[]
  usersTotal: number
  usersLoading: boolean
  usersError: string | null
  userFilters: UserFilters
  usersPagination: {
    limit: number
    skip: number
    page: number
  }
  selectedUser: AdminUser | null

  // ==================== Risk Score Mapping State ====================
  riskMappings: RiskScoreMapping[]
  riskMappingsLoading: boolean
  riskMappingsError: string | null
  selectedRiskMapping: RiskScoreMapping | null
  riskMappingImpact: RiskMappingImpactPreview | null

  // ==================== Questionnaire Approval State ====================
  pendingQuestionnaires: PendingQuestionnaire[]
  pendingQuestionnairesTotal: number
  pendingQuestionnairesLoading: boolean
  pendingQuestionnairesError: string | null
  selectedQuestionnaire: QuestionnaireWithUserDetails | null

  // ==================== Audit Log State ====================
  auditLogs: AuditLog[]
  auditLogsTotal: number
  auditLogsLoading: boolean
  auditLogsError: string | null
  auditLogFilters: Partial<AuditLogQuery>
  auditLogsPagination: {
    limit: number
    skip: number
    page: number
  }
  auditStats: AuditLogStats | null

  // ==================== Cost State ====================
  userCosts: Record<string, UserCostSummary>
  userCostsLoading: boolean

  // ==================== Model Settings State ====================
  models: ClaudeModel[]
  modelsLoading: boolean
  modelsError: string | null
  currentDefaultModel: string | null

  // ==================== Budget State ====================
  budget: BudgetStatus | null
  budgetLoading: boolean
  budgetError: string | null

  // ==================== Subscription Revenue & Pricing State ====================
  revenue: RevenueReport | null
  revenueLoading: boolean
  revenueError: string | null
  subscriptionPrices: SubscriptionPrices | null
  pricesLoading: boolean
  pricesError: string | null
  subscribers: AdminSubscriber[] | null
  subscribersLoading: boolean
  subscribersError: string | null
  coupons: Coupon[] | null
  couponsLoading: boolean
  couponsError: string | null

  // ==================== User Management Actions ====================
  fetchUsers: (query?: UserListQuery) => Promise<void>
  fetchUserById: (id: string) => Promise<void>
  updateUserStatus: (id: string, data: UpdateUserStatusRequest) => Promise<void>
  sendPasswordReset: (id: string) => Promise<void>
  unlockUser: (id: string) => Promise<void>
  verifyUserEmail: (id: string) => Promise<void>
  deleteUser: (id: string, data: { confirmedName: string; reason?: string }) => Promise<void>
  setUserFilters: (filters: Partial<UserFilters>) => void
  setUsersPagination: (pagination: Partial<AdminState['usersPagination']>) => void
  clearUsersError: () => void

  // ==================== Risk Mapping Actions ====================
  fetchRiskMappings: (includeArchived?: boolean) => Promise<void>
  fetchActiveRiskMappings: () => Promise<void>
  fetchRiskMappingById: (id: string) => Promise<void>
  createRiskMapping: (data: CreateRiskMappingRequest) => Promise<void>
  updateRiskMapping: (id: string, data: UpdateRiskMappingRequest) => Promise<void>
  deleteRiskMapping: (id: string) => Promise<void>
  approveRiskMapping: (id: string, data: ApproveRiskMappingRequest) => Promise<void>
  previewRiskMappingImpact: (id: string) => Promise<void>
  clearRiskMappingsError: () => void

  // ==================== Questionnaire Approval Actions ====================
  fetchPendingQuestionnaires: (query?: { limit?: number; skip?: number; minScore?: number }) => Promise<void>
  fetchQuestionnaireById: (id: string) => Promise<void>
  approveQuestionnaire: (id: string, data: QuestionnaireApprovalRequest) => Promise<void>
  clearPendingQuestionnairesError: () => void

  // ==================== Audit Log Actions ====================
  fetchAuditLogs: (query?: AuditLogQuery) => Promise<void>
  fetchAuditLogById: (id: string) => Promise<void>
  exportAuditLogs: (data: ExportAuditLogsRequest) => Promise<void>
  fetchAuditStats: () => Promise<void>
  setAuditLogFilters: (filters: Partial<AuditLogQuery>) => void
  setAuditLogsPagination: (pagination: Partial<AdminState['auditLogsPagination']>) => void
  clearAuditLogsError: () => void

  // ==================== Cost Actions ====================
  fetchUsersCosts: () => Promise<void>
  fetchUserCostBreakdown: (userId: string) => Promise<UserCostBreakdown | null>

  // ==================== Model Settings Actions ====================
  fetchModels: () => Promise<void>
  setDefaultModel: (modelId: string) => Promise<void>
  setUserModel: (userId: string, modelId: string | null) => Promise<void>

  // ==================== Budget Actions ====================
  fetchBudget: () => Promise<void>
  setMonthlyBudget: (monthlyBudgetUSD: number) => Promise<void>

  // ==================== Subscription Revenue & Pricing Actions ====================
  fetchRevenue: () => Promise<void>
  fetchSubscriptionPrices: () => Promise<void>
  setSubscriptionPrices: (prices: SubscriptionPrices) => Promise<void>
  fetchSubscribers: () => Promise<void>
  fetchCoupons: () => Promise<void>
  createCoupon: (input: CouponInput) => Promise<void>
  updateCoupon: (id: string, input: Partial<CouponInput>) => Promise<void>
  toggleCoupon: (id: string, active: boolean) => Promise<void>
  deleteCoupon: (id: string) => Promise<void>

  // ==================== Student learning tool ====================
  studentSettings: StudentSettings | null
  studentSettingsLoading: boolean
  studentSettingsError: string | null
  classCodes: ClassCode[]
  classCodesLoading: boolean
  classCodesError: string | null
  fetchStudentSettings: () => Promise<void>
  setStudentSettings: (settings: { studentSpendCapUSD: number; maxStudentAccounts: number }) => Promise<void>
  fetchClassCodes: () => Promise<void>
  createClassCode: (input: { code: string; label: string; seatLimit: number; expiresAt?: string }) => Promise<void>
  updateClassCode: (id: string, input: { label?: string; seatLimit?: number; expiresAt?: string | null }) => Promise<void>
  toggleClassCode: (id: string) => Promise<void>
  deleteClassCode: (id: string) => Promise<void>
  resetStudentAllowance: (userId: string) => Promise<void>

  // ==================== Learning path content (מסלול למידה) ====================
  learningTopics: LearningTopicAdmin[]
  learningTopicsLoading: boolean
  learningTopicsError: string | null
  examQuestions: ExamQuestionAdmin[]
  examQuestionsLoading: boolean
  examQuestionsError: string | null
  classProgress: ClassProgressRow[]
  classProgressSummary: ClassProgressSummary | null
  classProgressLoading: boolean
  classProgressError: string | null
  /** Installs the shipped default curriculum. force=true overwrites admin edits. */
  // Content actions take the TRACK they operate on. It defaults to 'student', so
  // every pre-existing caller keeps editing the school curriculum unchanged.
  seedLearningContent: (force?: boolean, track?: TrackKey) => Promise<string>
  fetchLearningTopics: (track?: TrackKey) => Promise<void>
  createLearningTopic: (input: Partial<LearningTopicAdmin>, track?: TrackKey) => Promise<void>
  updateLearningTopic: (id: string, input: Partial<LearningTopicAdmin>, track?: TrackKey) => Promise<void>
  toggleLearningTopic: (id: string, track?: TrackKey) => Promise<void>
  deleteLearningTopic: (id: string, track?: TrackKey) => Promise<void>

  // Portal tracks (מסלולים)
  portalTracks: TrackAdmin[]
  portalTracksLoading: boolean
  portalTracksError: string | null
  fetchPortalTracks: () => Promise<void>
  updatePortalTrack: (track: TrackKey, input: Partial<TrackAdmin>) => Promise<string | undefined>
  restoreTrackPersona: (track: TrackKey) => Promise<void>

  // "What did people actually ask?" — the training loop
  trackQuestions: TrackQuestionRow[]
  trackQuestionsLoading: boolean
  trackQuestionsError: string | null
  fetchTrackQuestions: (track: TrackKey, params?: { days?: number; search?: string }) => Promise<void>
  fetchExamQuestions: (topicSlug?: string) => Promise<void>
  createExamQuestion: (input: Partial<ExamQuestionAdmin>) => Promise<void>
  updateExamQuestion: (id: string, input: Partial<ExamQuestionAdmin>) => Promise<void>
  toggleExamQuestion: (id: string) => Promise<void>
  deleteExamQuestion: (id: string) => Promise<void>
  fetchClassProgress: (classCodeId?: string) => Promise<void>
  exportClassProgress: (classCodeId?: string) => Promise<void>

  // Section admins (מנהלי אזור) — admin-only
  subAdmins: SubAdmin[]
  assignableTracks: AssignableTrack[]
  subAdminsLoading: boolean
  subAdminsError: string | null
  fetchSubAdmins: () => Promise<void>
  createSubAdmin: (input: {
    email: string
    firstName: string
    lastName: string
    tracks: string[]
  }) => Promise<string>
  grantSubAdmin: (input: { email: string; tracks: string[] }) => Promise<string>
  updateSubAdminTracks: (id: string, tracks: string[]) => Promise<void>
  resendSubAdminInvite: (id: string) => Promise<string>
  revokeSubAdmin: (id: string) => Promise<void>
}


/**
 * Admin content endpoints. The student track keeps the original /admin/learning
 * paths so nothing that existed before tracks changes shape; the new tracks use
 * the generic /admin/tracks/:track routes.
 */
const adminTrackBase = (track: TrackKey = 'student') =>
  track === 'student' ? '/admin/learning' : `/admin/tracks/${track}`

export const useAdminStore = create<AdminState>((set, get) => ({
  // ==================== Initial State ====================

  // User Management
  users: [],
  usersTotal: 0,
  usersLoading: false,
  usersError: null,
  userFilters: {},
  usersPagination: {
    limit: 20,
    skip: 0,
    page: 1
  },
  selectedUser: null,

  // Risk Mappings
  riskMappings: [],
  riskMappingsLoading: false,
  riskMappingsError: null,
  selectedRiskMapping: null,
  riskMappingImpact: null,

  // Questionnaires
  pendingQuestionnaires: [],
  pendingQuestionnairesTotal: 0,
  pendingQuestionnairesLoading: false,
  pendingQuestionnairesError: null,
  selectedQuestionnaire: null,

  // Audit Logs
  auditLogs: [],
  auditLogsTotal: 0,
  auditLogsLoading: false,
  auditLogsError: null,
  auditLogFilters: {},
  auditLogsPagination: {
    limit: 50,
    skip: 0,
    page: 1
  },
  auditStats: null,

  // Costs
  userCosts: {},
  userCostsLoading: false,

  // Model Settings
  models: [],
  modelsLoading: false,
  modelsError: null,
  currentDefaultModel: null,

  // Budget
  budget: null,
  budgetLoading: false,
  budgetError: null,

  revenue: null,
  revenueLoading: false,
  revenueError: null,
  subscriptionPrices: null,
  pricesLoading: false,
  subscribers: null,
  subscribersLoading: false,
  subscribersError: null,
  coupons: null,
  couponsLoading: false,
  couponsError: null,
  pricesError: null,

  // Student learning tool
  studentSettings: null,
  studentSettingsLoading: false,
  studentSettingsError: null,
  classCodes: [],
  classCodesLoading: false,
  classCodesError: null,

  // Learning path content
  learningTopics: [],
  learningTopicsLoading: false,
  learningTopicsError: null,
  examQuestions: [],
  examQuestionsLoading: false,
  examQuestionsError: null,
  classProgress: [],
  classProgressSummary: null,
  classProgressLoading: false,
  classProgressError: null,

  // ==================== User Management Actions ====================

  fetchUsers: async (query?: UserListQuery) => {
    set({ usersLoading: true, usersError: null })
    try {
      const { userFilters, usersPagination } = get()

      const params = {
        ...userFilters,
        ...usersPagination,
        ...query
      }

      const response = await api.get<AdminUsersResponse>('/admin/users', { params })

      set({
        users: response.data.users,
        usersTotal: response.data.total,
        usersPagination: {
          limit: response.data.limit,
          skip: response.data.skip,
          page: response.data.page
        },
        usersLoading: false
      })
    } catch (error) {
      set({
        usersError: handleApiError(error),
        usersLoading: false
      })
    }
  },

  fetchUserById: async (id: string) => {
    set({ usersLoading: true, usersError: null })
    try {
      const response = await api.get(`/admin/users/${id}`)

      set({
        selectedUser: response.data.user,
        usersLoading: false
      })
    } catch (error) {
      set({
        usersError: handleApiError(error),
        usersLoading: false
      })
    }
  },

  updateUserStatus: async (id: string, data: UpdateUserStatusRequest) => {
    set({ usersLoading: true, usersError: null })
    try {
      await api.put(`/admin/users/${id}/status`, data)

      // Refresh users list
      await get().fetchUsers()

      set({ usersLoading: false })
    } catch (error) {
      set({
        usersError: handleApiError(error),
        usersLoading: false
      })
      throw error
    }
  },

  sendPasswordReset: async (id: string) => {
    set({ usersLoading: true, usersError: null })
    try {
      await api.post(`/admin/users/${id}/reset-password`)
      set({ usersLoading: false })
    } catch (error) {
      set({
        usersError: handleApiError(error),
        usersLoading: false
      })
      throw error
    }
  },

  unlockUser: async (id: string) => {
    set({ usersLoading: true, usersError: null })
    try {
      await api.post(`/admin/users/${id}/unlock`)
      // Refresh so the lockout badge clears immediately
      await get().fetchUsers()
      set({ usersLoading: false })
    } catch (error) {
      set({
        usersError: handleApiError(error),
        usersLoading: false
      })
      throw error
    }
  },

  verifyUserEmail: async (id: string) => {
    set({ usersLoading: true, usersError: null })
    try {
      await api.post(`/admin/users/${id}/verify-email`)
      // Refresh so the "pending"/"unverified" badges clear immediately
      await get().fetchUsers()
      set({ usersLoading: false })
    } catch (error) {
      set({
        usersError: handleApiError(error),
        usersLoading: false
      })
      throw error
    }
  },

  deleteUser: async (id, data) => {
    set({ usersLoading: true, usersError: null })
    try {
      await api.delete(`/admin/users/${id}`, { data })
      await get().fetchUsers()
      set({ usersLoading: false })
    } catch (error) {
      set({
        usersError: handleApiError(error),
        usersLoading: false
      })
      throw error
    }
  },

  setUserFilters: (filters: Partial<UserFilters>) => {
    set((state) => ({
      userFilters: { ...state.userFilters, ...filters }
    }))
  },

  setUsersPagination: (pagination: Partial<AdminState['usersPagination']>) => {
    set((state) => ({
      usersPagination: { ...state.usersPagination, ...pagination }
    }))
  },

  clearUsersError: () => set({ usersError: null }),

  // ==================== Risk Mapping Actions ====================

  fetchRiskMappings: async (includeArchived = false) => {
    set({ riskMappingsLoading: true, riskMappingsError: null })
    try {
      const response = await api.get<RiskMappingsResponse>('/admin/risk-mappings', {
        params: { includeArchived }
      })

      set({
        riskMappings: response.data.mappings,
        riskMappingsLoading: false
      })
    } catch (error) {
      set({
        riskMappingsError: handleApiError(error),
        riskMappingsLoading: false
      })
    }
  },

  fetchActiveRiskMappings: async () => {
    set({ riskMappingsLoading: true, riskMappingsError: null })
    try {
      const response = await api.get<RiskMappingsResponse>('/admin/risk-mappings/active')

      set({
        riskMappings: response.data.mappings,
        riskMappingsLoading: false
      })
    } catch (error) {
      set({
        riskMappingsError: handleApiError(error),
        riskMappingsLoading: false
      })
    }
  },

  fetchRiskMappingById: async (id: string) => {
    set({ riskMappingsLoading: true, riskMappingsError: null })
    try {
      const response = await api.get(`/admin/risk-mappings/${id}`)

      set({
        selectedRiskMapping: response.data.mapping,
        riskMappingsLoading: false
      })
    } catch (error) {
      set({
        riskMappingsError: handleApiError(error),
        riskMappingsLoading: false
      })
    }
  },

  createRiskMapping: async (data: CreateRiskMappingRequest) => {
    set({ riskMappingsLoading: true, riskMappingsError: null })
    try {
      await api.post('/admin/risk-mappings', data)

      // Refresh mappings list
      await get().fetchRiskMappings()

      set({ riskMappingsLoading: false })
    } catch (error) {
      set({
        riskMappingsError: handleApiError(error),
        riskMappingsLoading: false
      })
      throw error
    }
  },

  updateRiskMapping: async (id: string, data: UpdateRiskMappingRequest) => {
    set({ riskMappingsLoading: true, riskMappingsError: null })
    try {
      await api.put(`/admin/risk-mappings/${id}`, data)

      // Refresh mappings list
      await get().fetchRiskMappings()

      set({ riskMappingsLoading: false })
    } catch (error) {
      set({
        riskMappingsError: handleApiError(error),
        riskMappingsLoading: false
      })
      throw error
    }
  },

  deleteRiskMapping: async (id: string) => {
    set({ riskMappingsLoading: true, riskMappingsError: null })
    try {
      await api.delete(`/admin/risk-mappings/${id}`)

      // Refresh mappings list
      await get().fetchRiskMappings()

      set({ riskMappingsLoading: false })
    } catch (error) {
      set({
        riskMappingsError: handleApiError(error),
        riskMappingsLoading: false
      })
      throw error
    }
  },

  approveRiskMapping: async (id: string, data: ApproveRiskMappingRequest) => {
    set({ riskMappingsLoading: true, riskMappingsError: null })
    try {
      await api.post(`/admin/risk-mappings/${id}/approve`, data)

      // Refresh mappings list
      await get().fetchRiskMappings()

      set({ riskMappingsLoading: false })
    } catch (error) {
      set({
        riskMappingsError: handleApiError(error),
        riskMappingsLoading: false
      })
      throw error
    }
  },

  previewRiskMappingImpact: async (id: string) => {
    set({ riskMappingsLoading: true, riskMappingsError: null })
    try {
      const response = await api.get<{ message: string } & RiskMappingImpactPreview>(
        `/admin/risk-mappings/${id}/preview`
      )

      set({
        riskMappingImpact: response.data,
        riskMappingsLoading: false
      })
    } catch (error) {
      set({
        riskMappingsError: handleApiError(error),
        riskMappingsLoading: false
      })
    }
  },

  clearRiskMappingsError: () => set({ riskMappingsError: null }),

  // ==================== Questionnaire Approval Actions ====================

  fetchPendingQuestionnaires: async (query?) => {
    set({ pendingQuestionnairesLoading: true, pendingQuestionnairesError: null })
    try {
      const response = await api.get<PendingQuestionnairesResponse>(
        '/admin/questionnaires/pending',
        { params: query }
      )

      set({
        pendingQuestionnaires: response.data.questionnaires,
        pendingQuestionnairesTotal: response.data.total,
        pendingQuestionnairesLoading: false
      })
    } catch (error) {
      set({
        pendingQuestionnairesError: handleApiError(error),
        pendingQuestionnairesLoading: false
      })
    }
  },

  fetchQuestionnaireById: async (id: string) => {
    set({ pendingQuestionnairesLoading: true, pendingQuestionnairesError: null })
    try {
      const response = await api.get(`/admin/questionnaires/${id}`)

      set({
        selectedQuestionnaire: response.data.questionnaire,
        pendingQuestionnairesLoading: false
      })
    } catch (error) {
      set({
        pendingQuestionnairesError: handleApiError(error),
        pendingQuestionnairesLoading: false
      })
    }
  },

  approveQuestionnaire: async (id: string, data: QuestionnaireApprovalRequest) => {
    set({ pendingQuestionnairesLoading: true, pendingQuestionnairesError: null })
    try {
      await api.post(`/admin/questionnaires/${id}/approve`, data)

      // Refresh pending questionnaires list
      await get().fetchPendingQuestionnaires()

      set({ pendingQuestionnairesLoading: false })
    } catch (error) {
      set({
        pendingQuestionnairesError: handleApiError(error),
        pendingQuestionnairesLoading: false
      })
      throw error
    }
  },

  clearPendingQuestionnairesError: () => set({ pendingQuestionnairesError: null }),

  // ==================== Audit Log Actions ====================

  fetchAuditLogs: async (query?: AuditLogQuery) => {
    set({ auditLogsLoading: true, auditLogsError: null })
    try {
      const { auditLogFilters, auditLogsPagination } = get()

      const params = {
        ...auditLogFilters,
        ...auditLogsPagination,
        ...query
      }

      const response = await api.get<AuditLogsResponse>('/admin/audit-logs', { params })

      set({
        auditLogs: response.data.logs,
        auditLogsTotal: response.data.total,
        auditLogsPagination: {
          limit: response.data.limit,
          skip: response.data.skip,
          page: response.data.page
        },
        auditLogsLoading: false
      })
    } catch (error) {
      set({
        auditLogsError: handleApiError(error),
        auditLogsLoading: false
      })
    }
  },

  fetchAuditLogById: async (id: string) => {
    set({ auditLogsLoading: true, auditLogsError: null })
    try {
      await api.get(`/admin/audit-logs/${id}`)

      // Store in auditLogs array (could also add selectedAuditLog if needed)
      set({
        auditLogsLoading: false
      })
    } catch (error) {
      set({
        auditLogsError: handleApiError(error),
        auditLogsLoading: false
      })
    }
  },

  exportAuditLogs: async (data: ExportAuditLogsRequest) => {
    set({ auditLogsLoading: true, auditLogsError: null })
    try {
      const response = await api.post('/admin/audit-logs/export', data, {
        responseType: data.format === 'csv' ? 'blob' : 'json'
      })

      if (data.format === 'csv') {
        // Download CSV file
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `audit-logs-${Date.now()}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }

      set({ auditLogsLoading: false })
    } catch (error) {
      set({
        auditLogsError: handleApiError(error),
        auditLogsLoading: false
      })
      throw error
    }
  },

  fetchAuditStats: async () => {
    set({ auditLogsLoading: true, auditLogsError: null })
    try {
      const response = await api.get<AuditStatsResponse>('/admin/audit-logs/stats')

      set({
        auditStats: response.data.stats,
        auditLogsLoading: false
      })
    } catch (error) {
      set({
        auditLogsError: handleApiError(error),
        auditLogsLoading: false
      })
    }
  },

  setAuditLogFilters: (filters: Partial<AuditLogQuery>) => {
    set((state) => ({
      auditLogFilters: { ...state.auditLogFilters, ...filters }
    }))
  },

  setAuditLogsPagination: (pagination: Partial<AdminState['auditLogsPagination']>) => {
    set((state) => ({
      auditLogsPagination: { ...state.auditLogsPagination, ...pagination }
    }))
  },

  clearAuditLogsError: () => set({ auditLogsError: null }),

  // ==================== Cost Actions ====================

  fetchUsersCosts: async () => {
    set({ userCostsLoading: true })
    try {
      const response = await api.get<{ costs: Record<string, UserCostSummary> }>('/admin/users/costs')
      set({ userCosts: response.data.costs, userCostsLoading: false })
    } catch {
      set({ userCostsLoading: false })
    }
  },

  fetchUserCostBreakdown: async (userId: string): Promise<UserCostBreakdown | null> => {
    try {
      const response = await api.get<UserCostBreakdown>(`/admin/users/${userId}/costs`)
      return response.data
    } catch {
      return null
    }
  },

  // ==================== Model Settings Actions ====================

  fetchModels: async () => {
    set({ modelsLoading: true, modelsError: null })
    try {
      const response = await api.get<ModelsResponse>('/admin/models')
      set({
        models: response.data.models,
        currentDefaultModel: response.data.currentDefault,
        modelsLoading: false
      })
    } catch (error) {
      set({ modelsError: handleApiError(error), modelsLoading: false })
    }
  },

  setDefaultModel: async (modelId: string) => {
    set({ modelsLoading: true, modelsError: null })
    try {
      await api.put('/admin/models/default', { modelId })
      set({ currentDefaultModel: modelId, modelsLoading: false })
    } catch (error) {
      set({ modelsError: handleApiError(error), modelsLoading: false })
    }
  },

  setUserModel: async (userId: string, modelId: string | null) => {
    try {
      await api.put(`/admin/users/${userId}/model`, { modelId })
      // Refresh user list to reflect the change
      const { fetchUsers } = get()
      await fetchUsers()
    } catch (error) {
      set({ usersError: handleApiError(error) })
    }
  },

  // ==================== Budget Actions ====================

  fetchBudget: async () => {
    set({ budgetLoading: true, budgetError: null })
    try {
      const response = await api.get<BudgetStatus>('/admin/budget')
      set({ budget: response.data, budgetLoading: false })
    } catch (error) {
      set({ budgetError: handleApiError(error), budgetLoading: false })
    }
  },

  setMonthlyBudget: async (monthlyBudgetUSD: number) => {
    set({ budgetLoading: true, budgetError: null })
    try {
      const response = await api.put<BudgetStatus & { message: string }>('/admin/budget', { monthlyBudgetUSD })
      const { message: _msg, ...status } = response.data
      set({ budget: status, budgetLoading: false })
    } catch (error) {
      set({ budgetError: handleApiError(error), budgetLoading: false })
      throw error
    }
  },

  // ==================== Subscription Revenue & Pricing Actions ====================

  fetchRevenue: async () => {
    set({ revenueLoading: true, revenueError: null })
    try {
      const response = await api.get<RevenueReport>('/admin/revenue')
      set({ revenue: response.data, revenueLoading: false })
    } catch (error) {
      set({ revenueError: handleApiError(error), revenueLoading: false })
    }
  },

  fetchSubscriptionPrices: async () => {
    set({ pricesLoading: true, pricesError: null })
    try {
      const response = await api.get<SubscriptionPrices>('/admin/subscription-prices')
      set({ subscriptionPrices: response.data, pricesLoading: false })
    } catch (error) {
      set({ pricesError: handleApiError(error), pricesLoading: false })
    }
  },

  setSubscriptionPrices: async (prices: SubscriptionPrices) => {
    set({ pricesLoading: true, pricesError: null })
    try {
      const response = await api.put<SubscriptionPrices & { message: string }>(
        '/admin/subscription-prices',
        prices
      )
      const { message: _msg, ...updated } = response.data
      set({ subscriptionPrices: updated, pricesLoading: false })
    } catch (error) {
      set({ pricesError: handleApiError(error), pricesLoading: false })
      throw error
    }
  },

  fetchSubscribers: async () => {
    set({ subscribersLoading: true, subscribersError: null })
    try {
      const response = await api.get<SubscribersResponse>('/admin/subscribers')
      set({ subscribers: response.data.subscribers, subscribersLoading: false })
    } catch (error) {
      set({ subscribersError: handleApiError(error), subscribersLoading: false })
    }
  },

  fetchCoupons: async () => {
    set({ couponsLoading: true, couponsError: null })
    try {
      const response = await api.get<{ coupons: Coupon[] }>('/admin/coupons')
      set({ coupons: response.data.coupons, couponsLoading: false })
    } catch (error) {
      set({ couponsError: handleApiError(error), couponsLoading: false })
    }
  },

  createCoupon: async (input: CouponInput) => {
    set({ couponsError: null })
    try {
      await api.post('/admin/coupons', input)
      await get().fetchCoupons()
    } catch (error) {
      set({ couponsError: handleApiError(error) })
      throw error
    }
  },

  updateCoupon: async (id: string, input: Partial<CouponInput>) => {
    set({ couponsError: null })
    try {
      await api.put(`/admin/coupons/${id}`, input)
      await get().fetchCoupons()
    } catch (error) {
      set({ couponsError: handleApiError(error) })
      throw error
    }
  },

  toggleCoupon: async (id: string, active: boolean) => {
    set({ couponsError: null })
    try {
      await api.post(`/admin/coupons/${id}/toggle-active`, { active })
      await get().fetchCoupons()
    } catch (error) {
      set({ couponsError: handleApiError(error) })
      throw error
    }
  },

  deleteCoupon: async (id: string) => {
    set({ couponsError: null })
    try {
      await api.delete(`/admin/coupons/${id}`)
      await get().fetchCoupons()
    } catch (error) {
      set({ couponsError: handleApiError(error) })
      throw error
    }
  },

  // ==================== Student learning tool ====================

  fetchStudentSettings: async () => {
    set({ studentSettingsLoading: true, studentSettingsError: null })
    try {
      const response = await api.get<StudentSettings>('/admin/student-settings')
      set({ studentSettings: response.data, studentSettingsLoading: false })
    } catch (error) {
      set({ studentSettingsError: handleApiError(error), studentSettingsLoading: false })
    }
  },

  setStudentSettings: async (settings: { studentSpendCapUSD: number; maxStudentAccounts: number }) => {
    set({ studentSettingsLoading: true, studentSettingsError: null })
    try {
      await api.put('/admin/student-settings', settings)
      await get().fetchStudentSettings()
    } catch (error) {
      set({ studentSettingsError: handleApiError(error), studentSettingsLoading: false })
      throw error
    }
  },

  fetchClassCodes: async () => {
    set({ classCodesLoading: true, classCodesError: null })
    try {
      const response = await api.get<{ classCodes: ClassCode[] }>('/admin/class-codes')
      set({ classCodes: response.data.classCodes, classCodesLoading: false })
    } catch (error) {
      set({ classCodesError: handleApiError(error), classCodesLoading: false })
    }
  },

  createClassCode: async (input) => {
    set({ classCodesError: null })
    try {
      await api.post('/admin/class-codes', input)
      await get().fetchClassCodes()
    } catch (error) {
      set({ classCodesError: handleApiError(error) })
      throw error
    }
  },

  updateClassCode: async (id, input) => {
    set({ classCodesError: null })
    try {
      await api.put(`/admin/class-codes/${id}`, input)
      await get().fetchClassCodes()
    } catch (error) {
      set({ classCodesError: handleApiError(error) })
      throw error
    }
  },

  toggleClassCode: async (id) => {
    set({ classCodesError: null })
    try {
      await api.post(`/admin/class-codes/${id}/toggle-active`)
      await get().fetchClassCodes()
    } catch (error) {
      set({ classCodesError: handleApiError(error) })
      throw error
    }
  },

  deleteClassCode: async (id) => {
    set({ classCodesError: null })
    try {
      await api.delete(`/admin/class-codes/${id}`)
      await get().fetchClassCodes()
    } catch (error) {
      set({ classCodesError: handleApiError(error) })
      throw error
    }
  },

  resetStudentAllowance: async (userId: string) => {
    await api.post(`/admin/users/${userId}/reset-student-allowance`)
  },

  // ==================== Learning path content actions ====================

  seedLearningContent: async (force = false, track = 'student') => {
    set({ learningTopicsError: null })
    try {
      const response = await api.post<{ message: string }>(`${adminTrackBase(track)}/seed`, { force })
      await get().fetchLearningTopics(track)
      if (track === 'student') await get().fetchExamQuestions()
      return response.data.message
    } catch (error) {
      set({ learningTopicsError: handleApiError(error) })
      throw error
    }
  },

  fetchLearningTopics: async (track = 'student') => {
    set({ learningTopicsLoading: true, learningTopicsError: null })
    try {
      const response = await api.get<{ topics: LearningTopicAdmin[] }>(
        `${adminTrackBase(track)}/topics`
      )
      set({ learningTopics: response.data.topics, learningTopicsLoading: false })
    } catch (error) {
      set({ learningTopicsError: handleApiError(error), learningTopicsLoading: false })
    }
  },

  createLearningTopic: async (input, track = 'student') => {
    set({ learningTopicsError: null })
    try {
      await api.post(`${adminTrackBase(track)}/topics`, input)
      await get().fetchLearningTopics(track)
    } catch (error) {
      set({ learningTopicsError: handleApiError(error) })
      throw error
    }
  },

  updateLearningTopic: async (id, input, track = 'student') => {
    set({ learningTopicsError: null })
    try {
      await api.put(`${adminTrackBase(track)}/topics/${id}`, input)
      await get().fetchLearningTopics(track)
    } catch (error) {
      set({ learningTopicsError: handleApiError(error) })
      throw error
    }
  },

  toggleLearningTopic: async (id, track = 'student') => {
    set({ learningTopicsError: null })
    try {
      await api.post(`${adminTrackBase(track)}/topics/${id}/toggle-active`)
      await get().fetchLearningTopics(track)
    } catch (error) {
      set({ learningTopicsError: handleApiError(error) })
      throw error
    }
  },

  deleteLearningTopic: async (id, track = 'student') => {
    set({ learningTopicsError: null })
    try {
      await api.delete(`${adminTrackBase(track)}/topics/${id}`)
      await get().fetchLearningTopics(track)
    } catch (error) {
      set({ learningTopicsError: handleApiError(error) })
      throw error
    }
  },

  // ==================== Portal tracks ====================

  portalTracks: [],
  portalTracksLoading: false,
  portalTracksError: null,

  fetchPortalTracks: async () => {
    set({ portalTracksLoading: true, portalTracksError: null })
    try {
      const response = await api.get<{ tracks: TrackAdmin[] }>('/admin/tracks')
      set({ portalTracks: response.data.tracks, portalTracksLoading: false })
    } catch (error) {
      set({ portalTracksError: handleApiError(error), portalTracksLoading: false })
    }
  },

  /** Returns the server's warning (e.g. persona too short to be cached), if any. */
  updatePortalTrack: async (track, input) => {
    set({ portalTracksError: null })
    try {
      const response = await api.put<{ warning?: string }>(`/admin/tracks/${track}`, input)
      await get().fetchPortalTracks()
      return response.data.warning
    } catch (error) {
      set({ portalTracksError: handleApiError(error) })
      throw error
    }
  },

  restoreTrackPersona: async (track) => {
    set({ portalTracksError: null })
    try {
      await api.post(`/admin/tracks/${track}/restore-persona`)
      await get().fetchPortalTracks()
    } catch (error) {
      set({ portalTracksError: handleApiError(error) })
      throw error
    }
  },

  // ==================== Track questions ====================

  trackQuestions: [],
  trackQuestionsLoading: false,
  trackQuestionsError: null,

  fetchTrackQuestions: async (track, params) => {
    set({ trackQuestionsLoading: true, trackQuestionsError: null })
    try {
      const response = await api.get<{ questions: TrackQuestionRow[] }>(
        `/admin/tracks/${track}/questions`,
        { params }
      )
      set({ trackQuestions: response.data.questions, trackQuestionsLoading: false })
    } catch (error) {
      set({ trackQuestionsError: handleApiError(error), trackQuestionsLoading: false })
    }
  },

  fetchExamQuestions: async (topicSlug?: string) => {
    set({ examQuestionsLoading: true, examQuestionsError: null })
    try {
      const response = await api.get<{ questions: ExamQuestionAdmin[] }>('/admin/learning/questions', {
        params: topicSlug ? { topicSlug } : undefined,
      })
      set({ examQuestions: response.data.questions, examQuestionsLoading: false })
    } catch (error) {
      set({ examQuestionsError: handleApiError(error), examQuestionsLoading: false })
    }
  },

  createExamQuestion: async (input) => {
    set({ examQuestionsError: null })
    try {
      await api.post('/admin/learning/questions', input)
      await get().fetchExamQuestions()
    } catch (error) {
      set({ examQuestionsError: handleApiError(error) })
      throw error
    }
  },

  updateExamQuestion: async (id, input) => {
    set({ examQuestionsError: null })
    try {
      await api.put(`/admin/learning/questions/${id}`, input)
      await get().fetchExamQuestions()
    } catch (error) {
      set({ examQuestionsError: handleApiError(error) })
      throw error
    }
  },

  toggleExamQuestion: async (id) => {
    set({ examQuestionsError: null })
    try {
      await api.post(`/admin/learning/questions/${id}/toggle-active`)
      await get().fetchExamQuestions()
    } catch (error) {
      set({ examQuestionsError: handleApiError(error) })
      throw error
    }
  },

  deleteExamQuestion: async (id) => {
    set({ examQuestionsError: null })
    try {
      await api.delete(`/admin/learning/questions/${id}`)
      await get().fetchExamQuestions()
    } catch (error) {
      set({ examQuestionsError: handleApiError(error) })
      throw error
    }
  },

  fetchClassProgress: async (classCodeId?: string) => {
    set({ classProgressLoading: true, classProgressError: null })
    try {
      const response = await api.get<{ rows: ClassProgressRow[]; summary: ClassProgressSummary }>(
        '/admin/learning/progress',
        { params: classCodeId ? { classCodeId } : undefined }
      )
      set({
        classProgress: response.data.rows,
        classProgressSummary: response.data.summary,
        classProgressLoading: false,
      })
    } catch (error) {
      set({ classProgressError: handleApiError(error), classProgressLoading: false })
    }
  },

  exportClassProgress: async (classCodeId?: string) => {
    set({ classProgressError: null })
    try {
      const response = await api.get('/admin/learning/progress/export', {
        params: classCodeId ? { classCodeId } : undefined,
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `learning-progress-${Date.now()}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      set({ classProgressError: handleApiError(error) })
      throw error
    }
  },

  // ==================== Section admins (מנהלי אזור) ====================

  subAdmins: [],
  assignableTracks: [],
  subAdminsLoading: false,
  subAdminsError: null,

  fetchSubAdmins: async () => {
    set({ subAdminsLoading: true, subAdminsError: null })
    try {
      const response = await api.get<SubAdminsResponse>('/admin/sub-admins')
      set({
        subAdmins: response.data.subAdmins,
        assignableTracks: response.data.assignableTracks,
        subAdminsLoading: false,
      })
    } catch (error) {
      set({ subAdminsError: handleApiError(error), subAdminsLoading: false })
    }
  },

  createSubAdmin: async (input) => {
    set({ subAdminsError: null })
    try {
      const response = await api.post<{ message: string }>('/admin/sub-admins', input)
      await get().fetchSubAdmins()
      return response.data.message
    } catch (error) {
      set({ subAdminsError: handleApiError(error) })
      throw error
    }
  },

  grantSubAdmin: async (input) => {
    set({ subAdminsError: null })
    try {
      const response = await api.post<{ message: string }>('/admin/sub-admins/grant', input)
      await get().fetchSubAdmins()
      return response.data.message
    } catch (error) {
      set({ subAdminsError: handleApiError(error) })
      throw error
    }
  },

  updateSubAdminTracks: async (id, tracks) => {
    set({ subAdminsError: null })
    try {
      await api.put(`/admin/sub-admins/${id}`, { tracks })
      await get().fetchSubAdmins()
    } catch (error) {
      set({ subAdminsError: handleApiError(error) })
      throw error
    }
  },

  resendSubAdminInvite: async (id) => {
    set({ subAdminsError: null })
    try {
      const response = await api.post<{ message: string }>(`/admin/sub-admins/${id}/invite`)
      return response.data.message
    } catch (error) {
      set({ subAdminsError: handleApiError(error) })
      throw error
    }
  },

  revokeSubAdmin: async (id) => {
    set({ subAdminsError: null })
    try {
      await api.delete(`/admin/sub-admins/${id}`)
      await get().fetchSubAdmins()
    } catch (error) {
      set({ subAdminsError: handleApiError(error) })
      throw error
    }
  },
}))
